import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, hasPermission } from '@/context/AuthContext';
import {
    Plus, Eye, Pencil, Trash2, Sparkles, Loader2, Search, Download,
    FileText, Calendar, User, X, Briefcase, Layers, Trash,
    Link2, Copy, Check, ExternalLink, Users,
} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import { toast } from 'sonner';
import PageHeader from '@/components/ux/PageHeader';
import ConfirmDialog from '@/components/ux/ConfirmDialog';
import ScreeningSkillsEditor from '@/components/templates/ScreeningSkillsEditor';
import Pagination from '@/components/ui/Pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 9;

/** API uses beginner | intermediate | advanced (legacy: medium) */
const DIFFICULTY_COLORS = {
    beginner: 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/20',
    intermediate: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20',
    advanced: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/20',
    medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20',
};
const DIFFICULTY_DISPLAY = {
    beginner: 'Easy',
    intermediate: 'Medium',
    advanced: 'Hard',
    medium: 'Medium',
};
const DIFFICULTY_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'beginner', label: 'Easy' },
    { value: 'intermediate', label: 'Medium' },
    { value: 'advanced', label: 'Hard' },
];

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const d = Math.floor(diff / 86400000);
    if (d < 1) return 'Today';
    if (d === 1) return 'Yesterday';
    if (d < 30) return `${d}d ago`;
    const m = Math.floor(d / 30);
    if (m < 12) return `${m}mo ago`;
    return `${Math.floor(m / 12)}y ago`;
}

/** Public URL a candidate opens — mirrors the backend's link builder. */
/** One labelled block in the job description viewer. */
function DetailBlock({ label, children }) {
    return (
        <div>
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
            {children}
        </div>
    );
}

/**
 * Everything defined on a role, not just its description text.
 *
 * The viewer used to receive the job description string alone, so the rounds,
 * screening checklist and pass threshold — the parts that decide how a
 * candidate is actually assessed — were invisible without opening the editor.
 */
function JDDetails({ tpl }) {
    if (!tpl) return null;

    const rounds = tpl.interviewRounds || [];
    const skills = tpl.screeningSkills || [];
    const totalMinutes = rounds.reduce((sum, r) => sum + (Number(r.durationMinutes) || 0), 0);
    const totalQuestions = rounds.reduce((sum, r) => sum + (Number(r.numberOfQuestions) || 0), 0);

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
                {[
                    ['Rounds', rounds.length || '—'],
                    ['Questions', totalQuestions || '—'],
                    ['Total time', totalMinutes ? `${totalMinutes} min` : '—'],
                    ['Pass mark', tpl.passThreshold == null ? 'Company default' : `${tpl.passThreshold}%`],
                ].map(([label, value]) => (
                    <div key={label} className="rounded-lg border border-border bg-muted/30 px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
                        <p className="text-sm font-semibold text-foreground">{value}</p>
                    </div>
                ))}
            </div>

            <DetailBlock label="Description">
                <div className="whitespace-pre-wrap break-words rounded-xl bg-muted/30 p-4 text-sm leading-relaxed text-foreground">
                    {tpl.jobDescription}
                </div>
            </DetailBlock>

            <DetailBlock label={`Interview rounds${rounds.length ? ` (${rounds.length})` : ''}`}>
                {rounds.length ? (
                    <div className="space-y-1.5">
                        {rounds.map((r, i) => (
                            <div key={r._id || i} className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border px-3 py-2 text-sm">
                                <span className="font-medium text-foreground">{i + 1}. {r.roundName}</span>
                                <span className="text-muted-foreground">·</span>
                                <span className="text-muted-foreground">{r.interviewTypeName}</span>
                                <span className="text-muted-foreground">·</span>
                                <span className="text-muted-foreground">{r.difficultyName}</span>
                                <span className="ml-auto text-xs text-muted-foreground">
                                    {r.numberOfQuestions} questions · {r.durationMinutes} min
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                        No rounds configured — interviews cannot be scheduled against this role until at least one is added.
                    </p>
                )}
            </DetailBlock>

            <DetailBlock label={`Screening skills${skills.length ? ` (${skills.length})` : ''}`}>
                {skills.length ? (
                    <div className="flex flex-wrap gap-1.5">
                        {skills.map(sk => (
                            <span key={sk.name} className={cn(
                                'rounded-full border px-2.5 py-1 text-xs font-medium',
                                sk.required
                                    ? 'border-primary/40 bg-primary/5 text-foreground'
                                    : 'border-border text-muted-foreground',
                            )}>
                                {sk.name}{sk.required && <span className="ml-1 text-[10px] uppercase text-primary">req</span>}
                            </span>
                        ))}
                    </div>
                ) : <p className="text-xs text-muted-foreground">None — candidates are not asked to self-assess.</p>}
            </DetailBlock>
        </div>
    );
}

/**
 * Opens a clean print view of the role and triggers the browser's print
 * dialog, where "Save as PDF" produces the file.
 *
 * Deliberately no PDF library — this gives properly laid-out output and adds
 * nothing to a bundle already over 2MB.
 */
function printJobDescription(tpl) {
    if (!tpl) return;
    const esc = (v) => String(v ?? '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const rounds = (tpl.interviewRounds || []).map((r, i) => `
        <tr>
            <td>${i + 1}. ${esc(r.roundName)}</td>
            <td>${esc(r.interviewTypeName)}</td>
            <td>${esc(r.difficultyName)}</td>
            <td>${esc(r.numberOfQuestions)}</td>
            <td>${esc(r.durationMinutes)} min</td>
        </tr>`).join('');

    const skills = (tpl.screeningSkills || [])
        .map(sk => `<li>${esc(sk.name)}${sk.required ? ' <em>(required)</em>' : ''}</li>`).join('');

    // A hidden iframe rather than a popup: window.open with 'noopener' returns
    // null by specification, so there was nothing to write to, and a plain
    // popup is blocked by default in most browsers anyway.
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(frame);

    const doc = frame.contentDocument;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8">
<title>${esc(tpl.title)}</title>
<style>
  @page { margin: 18mm; }
  body { font: 12pt/1.6 Georgia, 'Times New Roman', serif; color: #111; max-width: 760px; margin: 0 auto; }
  h1 { font-size: 20pt; margin: 0 0 4px; }
  .meta { color: #555; font-size: 10pt; margin-bottom: 20px; }
  h2 { font-size: 12pt; text-transform: uppercase; letter-spacing: .08em; color: #444;
       border-bottom: 1px solid #ddd; padding-bottom: 4px; margin: 24px 0 10px; }
  .jd { white-space: pre-wrap; }
  table { width: 100%; border-collapse: collapse; font-size: 10pt; }
  th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #eee; }
  th { color: #555; font-weight: 600; }
  ul { margin: 0; padding-left: 20px; }
  footer { margin-top: 28px; border-top: 1px solid #ddd; padding-top: 8px; font-size: 9pt; color: #777; }
</style></head><body>
<h1>${esc(tpl.title)}</h1>
<p class="meta">${[tpl.department, tpl.position].filter(Boolean).map(esc).join(' &middot; ')}</p>

<h2>Job description</h2>
<div class="jd">${esc(tpl.jobDescription)}</div>

${rounds ? `<h2>Interview process</h2>
<table><thead><tr><th>Round</th><th>Type</th><th>Difficulty</th><th>Questions</th><th>Duration</th></tr></thead>
<tbody>${rounds}</tbody></table>` : ''}

${skills ? `<h2>Screening skills</h2><ul>${skills}</ul>` : ''}

<footer>Generated from EchoHire on ${esc(new Date().toLocaleDateString())}</footer>
</body></html>`);
    doc.close();

    // Let the document lay out before the dialog measures it, then clean up
    // once printing is done or cancelled.
    const cleanup = () => setTimeout(() => frame.remove(), 500);
    setTimeout(() => {
        try {
            frame.contentWindow.focus();
            frame.contentWindow.onafterprint = cleanup;
            frame.contentWindow.print();
        } catch {
            toast.error('Could not open the print dialog.');
            frame.remove();
            return;
        }
        // onafterprint is unreliable in some browsers — remove it regardless.
        setTimeout(cleanup, 60000);
    }, 250);
}

function publicJobUrl(slug) {
    return `${window.location.origin}/jobs/${slug}`;
}

/**
 * Share-link controls on a job description card.
 *
 * Three states, in order of the admin's journey:
 *   1. No link yet          → single "Create job link" action
 *   2. Link live            → copyable URL + "close applications"
 *   3. Link closed          → same URL, greyed, + "reopen"
 *
 * The slug is minted once server-side and never changes, so a link already
 * posted to a job board keeps working across close/reopen cycles.
 */
function ShareJobLink({ tpl, onChange, canManage }) {
    const [busy, setBusy]     = useState(false);
    const [copied, setCopied] = useState(false);
    const [closeConfirm, setCloseConfirm] = useState(false);

    const hasLink = Boolean(tpl.publicSlug);
    const isLive  = hasLink && tpl.isPublic;
    const url     = hasLink ? publicJobUrl(tpl.publicSlug) : '';

    const create = async () => {
        setBusy(true);
        try {
            const res = await apiClient.generateJobPublicLink(tpl._id);
            onChange(tpl._id, { publicSlug: res.data.publicSlug, isPublic: true });
            toast.success('Job link is live — share it anywhere.');
        } catch (err) {
            toast.error(err.message || 'Could not create the job link.');
        } finally {
            setBusy(false);
        }
    };

    const toggle = async () => {
        setCloseConfirm(false);
        setBusy(true);
        try {
            const res = await apiClient.toggleJobPublicLink(tpl._id, !isLive);
            onChange(tpl._id, { isPublic: res.data.isPublic });
            toast.success(res.message);
        } catch (err) {
            toast.error(err.message || 'Could not update the job link.');
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            // Clipboard API needs a secure context; fall back to a temporary input.
            const input = document.createElement('input');
            input.value = url;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            input.remove();
        }
        setCopied(true);
        toast.success('Link copied to clipboard.');
        setTimeout(() => setCopied(false), 2000);
    };

    if (!canManage) return null;

    if (!hasLink) {
        return (
            <Button
                variant="outline"
                size="sm"
                className="h-7 w-full gap-1.5 text-[11px]"
                onClick={create}
                disabled={busy}
            >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
                Create job link
            </Button>
        );
    }

    return (
        <div className="space-y-1.5 rounded-lg border border-border bg-muted/30 p-2">
            <div className="flex items-center justify-between gap-2">
                <span className={cn(
                    'inline-flex items-center gap-1.5 text-[11px] font-medium',
                    isLive ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
                )}>
                    <span className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        isLive ? 'animate-pulse bg-emerald-500' : 'bg-muted-foreground/50',
                    )} />
                    {isLive ? 'Accepting applications' : 'Applications closed'}
                </span>
                <button
                    type="button"
                    onClick={() => (isLive ? setCloseConfirm(true) : toggle())}
                    disabled={busy}
                    className="text-[11px] font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
                >
                    {busy ? '…' : isLive ? 'Close' : 'Reopen'}
                </button>
            </div>

            {/* The slug itself is noise — what you actually want is to copy it
                or open it. */}
            <div className="flex items-center gap-1.5">
                <Button
                    variant="outline"
                    size="sm"
                    className="h-7 flex-1 gap-1.5 text-[11px]"
                    onClick={copy}
                >
                    {copied
                        ? <><Check className="h-3 w-3 text-emerald-500" /> Copied</>
                        : <><Copy className="h-3 w-3" /> Copy link</>}
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    className="h-7 flex-1 gap-1.5 text-[11px]"
                    asChild
                >
                    <a href={url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-3 w-3" /> Open
                    </a>
                </Button>
            </div>

            <ConfirmDialog
                open={closeConfirm}
                onOpenChange={setCloseConfirm}
                title="Close this opening?"
                description={
                    (tpl.applicantCount > 0
                        ? `${tpl.applicantCount} ${tpl.applicantCount === 1 ? 'person has' : 'people have'} applied. `
                        : '') +
                    'Closing stops new applications — the ones you already have are unaffected. ' +
                    'Anyone opening the link sees that the role has closed, and the job description is no longer shown. ' +
                    'You can reopen it at any time.'
                }
                confirmLabel="Close opening"
                loading={busy}
                onConfirm={toggle}
            />
        </div>
    );
}

function TemplateCard({ tpl, onEdit, onDelete, onView, onLinkChange, canManage, canDelete }) {
    const preview = tpl.jobDescription?.slice(0, 120) || '';
    const hasMore = (tpl.jobDescription?.length || 0) > 120;
    const creator = tpl.createdBy ? `${tpl.createdBy.firstName || ''} ${tpl.createdBy.lastName || ''}`.trim() : null;

    return (
        <div className="group flex flex-col rounded-xl border border-border bg-card transition-shadow hover:shadow-md">
            {/* Color accent bar */}
            <div className="h-1 w-full rounded-t-xl bg-gradient-to-r from-primary/60 via-primary/30 to-transparent" />

            <div className="flex flex-1 flex-col gap-3 p-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-2.5">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                            <FileText className="h-4 w-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">{tpl.title}</h3>
                            {tpl.department && (
                                <p className="mt-0.5 text-[11px] text-muted-foreground">{tpl.department}{tpl.position ? ` · ${tpl.position}` : ''}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* JD preview */}
                <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
                    {preview}{hasMore ? '…' : ''}
                </p>

                <div className="flex flex-wrap gap-1.5">
                    {(tpl.interviewRounds?.length > 0) && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                            <Layers className="h-3 w-3" />
                            {tpl.interviewRounds.length} round{tpl.interviewRounds.length !== 1 ? 's' : ''}
                        </span>
                    )}
                    {tpl.difficultyLevel && (
                        <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium', DIFFICULTY_COLORS[tpl.difficultyLevel] || DIFFICULTY_COLORS.intermediate)}>
                            {DIFFICULTY_DISPLAY[tpl.difficultyLevel] || tpl.difficultyLevel}
                        </span>
                    )}
                    {tpl.applicantCount > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-violet-500/25 bg-violet-500/10 px-2 py-0.5 text-[11px] font-medium text-violet-700 dark:text-violet-400">
                            <Users className="h-3 w-3" />
                            {tpl.applicantCount} applicant{tpl.applicantCount !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                <div className="mt-auto space-y-2.5">
                    <Separator />

                    <ShareJobLink tpl={tpl} onChange={onLinkChange} canManage={canManage} />

                    {/* Meta + actions */}
                    <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 space-y-0.5">
                            {creator && (
                                <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                    <User className="h-3 w-3 shrink-0" />
                                    <span className="truncate">{creator}</span>
                                </div>
                            )}
                            {tpl.createdAt && (
                                <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                    <Calendar className="h-3 w-3 shrink-0" />
                                    <span>{timeAgo(tpl.createdAt)}</span>
                                </div>
                            )}
                        </div>
                        <div className="flex shrink-0 gap-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                onClick={() => onView(tpl)}
                                aria-label="View job description"
                            >
                                <Eye className="h-3.5 w-3.5" />
                            </Button>
                            {canManage && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                onClick={() => onEdit(tpl)}
                                aria-label="Edit job description"
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            )}
                            {canDelete && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => onDelete(tpl)}
                                aria-label="Delete job description"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const EMPTY_FORM = { title: '', jobDescription: '', department: '', position: '', difficultyLevel: '', screeningSkills: [] };
const EMPTY_AI = { roleTitle: '', experienceLevel: '', requiredSkills: '', mustHaveRequirements: '', additionalContext: '' };
const EMPTY_ROUND = {
    roundMasterId: '',
    interviewTypeMasterId: '',
    difficultyMasterId: '',
    numberOfQuestions: '8',
    durationMinutes: '30',
};

function templateRoundFromApi(r) {
    return {
        roundMasterId: r.roundMasterId?._id || r.roundMasterId || '',
        interviewTypeMasterId: r.interviewTypeMasterId?._id || r.interviewTypeMasterId || '',
        difficultyMasterId: r.difficultyMasterId?._id || r.difficultyMasterId || '',
        numberOfQuestions: String(r.numberOfQuestions ?? 8),
        durationMinutes: String(r.durationMinutes ?? 30),
    };
}

const EXPERIENCE_LEVELS = ['Intern', 'Junior', 'Mid-Level', 'Senior', 'Lead', 'Principal'];

export default function TemplatesPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canManage = hasPermission(user, 'manage_templates');
    const canDelete = hasPermission(user, 'delete_templates');

    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);



    const [jdOpen, setJdOpen] = useState(false);
    const [selectedJD, setSelectedJD] = useState('');


    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    /** Patch one job description in place after a share-link action — avoids a full refetch. */
    const handleLinkChange = useCallback((templateId, patch) => {
        setTemplates(prev => prev.map(t => t._id === templateId ? { ...t, ...patch } : t));
    }, []);


    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient.getTemplates();
            setTemplates(res.data || []);
        } catch (err) {
            toast.error(err.message || 'Failed to load templates.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    const filtered = templates.filter(t => {
        if (!search) return true;
        const q = search.toLowerCase();
        return t.title?.toLowerCase().includes(q) || t.department?.toLowerCase().includes(q) || t.position?.toLowerCase().includes(q);
    });

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    useEffect(() => setPage(1), [search]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await apiClient.deleteTemplate(deleteTarget._id);
            toast.success(`"${deleteTarget.title}" deleted.`);
            setDeleteTarget(null);
            fetchTemplates();
        } catch (err) {
            toast.error(err.message || 'Failed to delete template.');
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Job Descriptions"
                description={`${templates.length} job description${templates.length !== 1 ? 's' : ''}`}
                actions={
                    canManage ? (
                    <Button onClick={() => navigate('/admin/templates/new')} className="gap-2">
                        <Plus className="h-4 w-4" />
                        New Job Description
                    </Button>
                    ) : null
                }
            />

            {/* Search bar */}
            <div className="relative max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    placeholder="Search job descriptions…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="h-10 pl-9 pr-9"
                />
                {search && (
                    <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {/* Template grid */}
            {loading ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="h-56 animate-pulse rounded-xl border border-border bg-muted/40" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-muted/20">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="text-center">
                        <p className="font-medium text-foreground">{search ? 'No job descriptions match your search' : 'No job descriptions yet'}</p>
                        <p className="text-sm text-muted-foreground">{search ? 'Try a different keyword' : 'Create your first job description to get started'}</p>
                    </div>
                    {!search && canManage && (
                        <Button onClick={() => navigate('/admin/templates/new')} className="gap-2">
                            <Plus className="h-4 w-4" />
                            Create First Job Description
                        </Button>
                    )}
                </div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {paged.map(tpl => (
                        <TemplateCard
                            key={tpl._id}
                            tpl={tpl}
                            onEdit={tpl => navigate(`/admin/templates/${tpl._id}/edit`)}
                            onDelete={setDeleteTarget}
                            onView={tpl => { setSelectedJD(tpl); setJdOpen(true); }}
                            onLinkChange={handleLinkChange}
                            canManage={canManage}
                            canDelete={canDelete}
                        />
                    ))}
                </div>
            )}

            <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={filtered.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
            />

            {/* Create / Edit Dialog */}

            {/* JD viewer — the whole role, not just its description */}
            <Dialog open={jdOpen} onOpenChange={o => !o && setJdOpen(false)}>
                <DialogContent className="flex max-h-[88vh] max-w-3xl flex-col gap-0 overflow-hidden">
                    <DialogHeader className="shrink-0">
                        <DialogTitle className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4 text-primary" />
                            {selectedJD?.title || 'Job description'}
                        </DialogTitle>
                        <DialogDescription>
                            {[selectedJD?.department, selectedJD?.position].filter(Boolean).join(' · ') || 'Full details for this role'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="no-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto py-2">
                        <JDDetails tpl={selectedJD} />
                    </div>

                    <DialogFooter className="mt-3 shrink-0 gap-2 border-t border-border pt-3">
                        <Button variant="outline" onClick={() => setJdOpen(false)}>Close</Button>
                        <Button className="gap-2" onClick={() => printJobDescription(selectedJD)}>
                            <Download className="h-4 w-4" /> Download PDF
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <ConfirmDialog
                open={Boolean(deleteTarget)}
                onOpenChange={o => { if (!o) setDeleteTarget(null); }}
                title="Delete job description"
                description={`Delete "${deleteTarget?.title}"? This action cannot be undone.`}
                confirmLabel="Delete"
                variant="destructive"
                loading={deleteLoading}
                onConfirm={handleDelete}
            />
        </div>
    );
}
